import React, { Component } from 'react';
import PropTypes from 'prop-types';
import numeral from 'numeral';
import 'utils/utils';
import { DateFormatter } from 'utils/DateFormatter';
import Constants from 'utils/Constants';
import { VictoryChart, VictoryLine, VictoryScatter, VictoryAxis, VictoryTooltip } from 'victory';
import styles from 'styles/components/series/tabbedGraphs/tabbedGraphContainer.css';
import theme from 'themes/victoryChartTheme';

export default class SalesRevenueGraph extends Component {
  shouldComponentUpdate(nProps, nState) {
    return !Object.deepEquals(nProps.data, this.props.data) ||
      nProps.salesRevenueGoal !== this.props.salesRevenueGoal;
  }
  changeDateFormat = () => {
    let data = this.props.data;
    let newData = [];
    if(data) {
	    newData = data.map((elem, index) => {
	      return {
          index: index,
          date: new Date(elem.label),
          value: elem.value,
          stringDate: elem.label,
          label: ''
        }
	    })
    }
    return newData;
  }
  getFormat = (tick) => {
    let format = (this.props.selectedUnit !== Constants.unit.month) ? 'TICK_DISPLAY' : 'MONTH_DISPLAY';
    return DateFormatter.format(tick, DateFormatter[format])
  }
  getGoalLine = () => {
    let { data, salesRevenueGoal, selectedUnit } = this.props
    let newData = [];
    if(data.length > 2) {
	    newData = data.map((elem, index) => {
        if (index !== data.length - 1) {
          return {
            index: index,
            date: new Date(elem.label),
            value: salesRevenueGoal
          }
        } else {
          return {
            index: index,
            date: new Date(elem.label),
            value: salesRevenueGoal,
            label: ''
          }
        }
	    })
    } else if (data.length === 2){
      newData = [
        {date: new Date(data[0].label), value: salesRevenueGoal, label: ''},
        {date: new Date(data[1].label), value: salesRevenueGoal}
      ]
    } else {
      if(selectedUnit === 'DAY') {
        newData = [
          {date: new Date(DateFormatter.getBefore(data[0].label, 'days')), value: salesRevenueGoal},
          {date: new Date(data[0].label), value: salesRevenueGoal},
          {date: new Date(DateFormatter.getAfter(data[0].label, 'days')), value: salesRevenueGoal, label: ''}
        ]
      } else if (selectedUnit === 'WEEK') {
        newData = [
          {date: new Date(DateFormatter.getBefore(data[0].label, 'weeks')), value: salesRevenueGoal},
          {date: new Date(data[0].label), value: salesRevenueGoal},
          {date: new Date(DateFormatter.getAfter(data[0].label, 'weeks')), value: salesRevenueGoal, label: ''}
        ]
      } else if (selectedUnit === 'MONTH') {
        newData = [
          {date: new Date(DateFormatter.getBefore(data[0].label, 'months')), value: salesRevenueGoal},
          {date: new Date(data[0].label), value: salesRevenueGoal},
          {date: new Date(DateFormatter.getAfter(data[0].label, 'months')), value: salesRevenueGoal, label: ''}
        ]
      }
    }
    return newData;
  }
  getSmallTickCount = (length) => {
    if (length < 3 ) return 1.5;
    else if (length < 10) return length;
    else return 10;
  }
  getTickValues = () => {
    let { data, selectedUnit } = this.props;
    if (selectedUnit === 'WEEK' && data.length < 11) {
      let newData = data.map((elem) => {
        return new Date(elem.label)
      })
      return newData;
    } else {
      return null;
    }
  }
  render() {
    let { chartSize, salesRevenue, salesRevenueGoal, selectedUnit } = this.props;
    let data = this.changeDateFormat();
    let goalLineData = this.getGoalLine();
    let length = data.length;
    let lastValue = data[length-1].value;
    if (lastValue < salesRevenueGoal) {
      lastValue = salesRevenueGoal;
    }
    let space = lastValue/4;
    let { width, height } = chartSize;
    if (data) {
      return (
        <div className={styles.graph} >
          <h6 className="printOnly">
            Cumulative Sales Revenue
            <span className="emPrimary">{numeral(salesRevenue).format('$0,0.00')}</span>
          </h6>
            <div style={{width: `${width}px`, height:`${height}px`}} >
              <VictoryChart
                width={width}
                height={height}
                domainPadding={{x:[10,130]}}
                theme={theme} >
                <VictoryAxis
                  scale="time"
                  style={{ tickLabels: {fill: (length > 1)
                      ? (tick) => (DateFormatter.isAfter(tick, data[length-1].date)) ? "transparent" : "#888"
                      : (tick) => (DateFormatter.isBefore(tick, data[length-1].date) || DateFormatter.isAfter(tick, data[length-1].date)) ? "transparent" : "#888"
                  } }}
                  tickCount={this.getSmallTickCount(length)}
                  tickValues={this.getTickValues()}
                  tickFormat={(x) => this.getFormat(x)} />
                <VictoryAxis
                  tickCount={5}
                  dependentAxis
                  tickFormat={(x) => (x) ? numeral(x).format('$0a') : null}
                />
                <VictoryLine
                  animate={{
                    duration: 2500,
                    onLoad: { duration: 2000 }
                  }}
                  data={data && data}
                  domain={{y:[0,lastValue+space]}}
                  labelComponent={<VictoryTooltip/>}
                  x="date"
                  y="value" />
                {salesRevenueGoal ? (
                  <VictoryLine
                    animate={{
                      duration: 2500,
                      onLoad: { duration: 2000 }
                    }}
                    data={goalLineData && goalLineData}
                    domain={{y:[0,lastValue+space]}}
                    labelComponent={
                      <CustomLabel length={goalLineData.length} goal={salesRevenueGoal}/>
                    }
                    style={{data: {strokeDasharray: [4]}}}
                    x="date"
                    y="value" />
                  ) : (
                    null
                  )
                }
                <VictoryScatter
                  animate={{
                    duration: 2500,
                    onLoad: { duration: 2000 }
                  }}
                  size={5}
                  data={data && data}
                  domain={{y:[0,lastValue+space]}}
                  labelComponent={
                    <VictoryTooltip
                      flyoutStyle={{
                        stroke: "#666", fill: "#3B454A"
                      }}
                      orientation={(datum) => {
                        if (datum.eventKey === 0) return "right";
                        else return "top"
                      }}
                      style={{textAnchor: 'start', fill: 'white'}}
                      text={(datum) => {
                          if (selectedUnit === 'MONTH') return `${DateFormatter.format(datum.date, DateFormatter.MONTH_DISPLAY)} - ${numeral(datum.value).format('$0,0.00')}`
                          return `${datum.stringDate} - ${numeral(datum.value).format('$0,0.00')}`
                        }
                      }

                    />
                  }
                  x="date"
                  y="value" />
              </VictoryChart>
            </div>
        </div>
      );
    }
  };
};

const CustomLabel = props => {
  if (props.length !== 2) {
    return (
      <g>
        <svg width='110' height='40' viewBox='0 0 50 17' x={props.x} y={props.y - 13.5}>
            <g id="Reporting-Dashboard-2" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <g id="Reporting-Dashboard-goal-line-v6" transform="translate(-552.000000, -549.000000)" fill="#0097F9">
                    <g id="Group-7" transform="translate(552.000000, 549.000000)">
                        <path d="M42.7944585,-32.847172 L49.5889169,-28.5859086 L49.5889169,-28.5859086 C50.1721717,-28.2201102 50.5262794,-27.5800373 50.5262794,-26.8915646 L50.5262794,48.3872296 L50.5262794,48.3872296 C50.5262794,49.4917991 49.6308489,50.3872296 48.5262794,50.3872296 L36,50.3872296 L36,50.3872296 C34.8954305,50.3872296 34,49.4917991 34,48.3872296 L34,-26.8915646 L34,-26.8915646 C34,-27.5800373 34.3541078,-28.2201102 34.9373625,-28.5859086 L41.731821,-32.847172 L41.731821,-32.847172 C42.0567273,-33.0509427 42.4695522,-33.0509427 42.7944585,-32.847172 Z" id="Polygon" transform="translate(42.263140, 8.693615) rotate(-90.000000) translate(-42.263140, -8.693615) "></path>
                    </g>
                </g>
            </g>
        </svg>
        <text fill="#FFFFFF" stroke="none" style={{fontSize: '10px'}} dx='1em' dy="2px" y={props.y + 1} >
          <tspan x={props.x} dx='1.25em' dy="0em" style ={{textTransform: 'none', fontWeight: 'bold'}}>
            Sales Goal
          </tspan>
          <tspan x={props.x} dx='1.25em' dy="1.75em" style ={{textTransform: 'none', fontWeight: 'bold'}}>
            <tspan style={{fontSize: '12px', fontWeight: 'bold'}}>{numeral(props.goal).format('$0,000')}</tspan>
          </tspan>
        </text>
      </g>
    );
  } else {
    return (
      <g>
        <svg width='110' height='40' viewBox='0 0 50 17' x={props.x} y={props.y - 13.5}>
            <g id="Reporting-Dashboard-2" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
                <g id="Reporting-Dashboard-goal-line-v6" transform="translate(-552.000000, -549.000000)" fill="#0097F9">
                    <g id="Group-7" transform="translate(552.000000, 549.000000)">
                        <path d="M42.7944585,-32.847172 L49.5889169,-28.5859086 L49.5889169,-28.5859086 C50.1721717,-28.2201102 50.5262794,-27.5800373 50.5262794,-26.8915646 L50.5262794,48.3872296 L50.5262794,48.3872296 C50.5262794,49.4917991 49.6308489,50.3872296 48.5262794,50.3872296 L36,50.3872296 L36,50.3872296 C34.8954305,50.3872296 34,49.4917991 34,48.3872296 L34,-26.8915646 L34,-26.8915646 C34,-27.5800373 34.3541078,-28.2201102 34.9373625,-28.5859086 L41.731821,-32.847172 L41.731821,-32.847172 C42.0567273,-33.0509427 42.4695522,-33.0509427 42.7944585,-32.847172 Z" id="Polygon" transform="translate(42.263140, 8.693615) rotate(-90.000000) translate(-42.263140, -8.693615) "></path>
                    </g>
                </g>
            </g>
        </svg>
        <text fill="#FFFFFF" stroke="none" style={{fontSize: '10px'}} dx='1em' dy="2px" y={props.y + 1} >
          <tspan x={props.x} dx='1.25rem' dy="0em" style ={{textTransform: 'none', fontWeight: 'bold'}}>
            Sales Goal
          </tspan>
          <tspan x={props.x} dx='1.25rem' dy="1.75em" style ={{textTransform: 'none', fontWeight: 'bold'}}>
            <tspan style={{fontSize: '12px', fontWeight: 'bold'}}>{numeral(props.goal).format('$0,000')}</tspan>
          </tspan>
        </text>
      </g>
    );
  }
};


SalesRevenueGraph.propTypes = {
  salesRevenue: PropTypes.number,
  salesRevenueGoal: PropTypes.number,
  data: PropTypes.array,
};

SalesRevenueGraph.contextTypes = {
  media: PropTypes.object
};



// WEBPACK FOOTER //
// ./src/scripts/components/series/tabbedGraphs/SalesRevenueGraph.js